import React from 'react';
import { useMobile, useOpenList, useLists } from '../../context';
import { useLang } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { useDismissible } from '../../hooks/useDismissible';
import { fmtDate } from '../../logic/dates';
import {
  CLOSING_REMINDER_HOUR, CLOSING_REMINDER_LS_KEY, ORDER_DAYS, ORDER_DAY_LS_KEY, VOICE_TIP_LS_KEY,
} from '../../config/constants';
import { Banner } from '../shared/Banner';

/**
 * The Today tab's nudges, top to bottom:
 *   📆 shopping day (Tue/Fri, dismissible per day) → opens the Shopping tab
 *   🌙 closing reminder (after closing time, nothing noted yet, once a day)
 *   🌙 the open list is from an earlier list-day → move it to History (not dismissible)
 *   ✨ items noted in a list their name no longer lives in → one-tap tidy
 *   🎤 "speak instead of typing" (touch only, once per device)
 */
export function TodayBanners({ total, hasHistory, today, misfiled, onTidy, onGoShopping }) {
  const { t, ln } = useLang();
  const { board, openMove } = useOpenList();
  const { catById } = useLists();
  const { name: listName } = useListNames();
  const isMobile = useMobile();

  const [orderDayOff, dismissOrderDay] = useDismissible(ORDER_DAY_LS_KEY, today);
  const [reminderOff, dismissReminder] = useDismissible(CLOSING_REMINDER_LS_KEY, today);
  const [voiceTipOff, dismissVoiceTip] = useDismissible(VOICE_TIP_LS_KEY, '1');
  const now = new Date();
  const showOrderDay = ORDER_DAYS.includes(now.getDay()) && !orderDayOff;
  const showReminder = !reminderOff && total === 0 && hasHistory && now.getHours() >= CLOSING_REMINDER_HOUR;
  const listDay = board ? board.listDay : today;

  return (
    <>
      {showOrderDay && (
        <Banner icon="📆" className="do-reminder--order" onDismiss={dismissOrderDay} dismissLabel={t('reminder.dismissShoppingDay')}
          action={onGoShopping && <button className="do-reminder__send" onClick={onGoShopping}>{t('reminder.openShopping')}</button>}>
          <b>{t('reminder.shoppingDayTitle')}</b>{' '}
          {t('reminder.shoppingDayBody', { day: t(now.getDay() === 2 ? 'reminder.dayTuesday' : 'reminder.dayFriday') })}
        </Banner>
      )}
      {showReminder && (
        <Banner icon="🌙" onDismiss={dismissReminder} dismissLabel={t('reminder.dismiss')}>{t('reminder.endOfDay')}</Banner>
      )}
      {board && board.stale && total > 0 && (
        <Banner icon="🌙" className="do-reminder--stale"
          action={<button className="do-reminder__send" onClick={openMove}>{t('list.moveBtn')}</button>}>
          <b>{t('list.staleTitle', { date: fmtDate(listDay) })}</b> {t('list.staleBody')}
        </Banner>
      )}
      {misfiled.length > 0 && (
        <Banner icon="✨" className="do-reminder--tidy"
          action={<button className="do-reminder__send" onClick={onTidy}>{t('tidy.btn')}</button>}>
          <b>{t('tidy.title', { n: misfiled.length })}</b>{' '}
          {t('tidy.example', { name: ln(misfiled[0].item.name), list: `${catById(misfiled[0].to).icon} ${listName(misfiled[0].to)}` })}
        </Banner>
      )}
      {isMobile && !voiceTipOff && (
        <Banner icon="🎤" onDismiss={dismissVoiceTip} dismissLabel={t('reminder.dismissVoice')}>
          <b>{t('reminder.voiceTitle')}</b>{t('reminder.voiceBody')}
        </Banner>
      )}
    </>
  );
}
